import {
  FanEngagementFragment,
  SlidesViewData,
  useCreateReactionMutation,
  useStageGetSubmissionQuery,
} from '@/generated/graphql';
import { useMemo } from 'react';
import { Markdown } from '@/components/Markdown';
import styles from './mobile-view.module.css';
import { useImageLoader } from '@/hooks/useImageLoader';
import { FanReactionButtons } from '@/components/FanReactionButtons';
import { cn } from '@/lib/utils';
import { normalizeSlideImageTilt } from '../slideLayout';
import { useDeviceSignalVariables } from '@/hooks/useDeviceIdentity';
import { isFreeformLayout } from '../slideFrame';
import { FanFreeformSlideCard } from './fanSlideCanvas';
import { useScreenTheme } from '@/providers/ScreenThemeStateProvider/EventScreenThemeContext';

const REACTIONS = ['👍', '🤩', '🎉', '👏'];

export const FanSlidesEngagement = ({
  engagement,
}: {
  engagement: FanEngagementFragment;
}) => {
  const currentSlideId = useMemo(() => {
    const viewData = engagement.viewData as SlidesViewData;
    return viewData.currentSlide;
  }, [engagement.viewData]);

  const { data } = useStageGetSubmissionQuery({
    skip: !currentSlideId,
    variables: { id: currentSlideId },
  });

  const slideData =
    data?.submission?.data?.__typename === 'SlidesSubmissionData'
      ? data.submission.data
      : null;

  const content = useMemo(() => {
    if (slideData) {
      return slideData.content;
    }
    return '';
  }, [slideData]);

  const imageUrl = useMemo(() => {
    if (slideData?.optionalImageUrl) {
      return slideData.optionalImageUrl;
    }
    return null;
  }, [slideData]);

  const { imageLoaded } = useImageLoader({ url: imageUrl });

  const imageTilt = useMemo(() => {
    if (slideData) {
      return normalizeSlideImageTilt(slideData.imageTilt);
    }
    return normalizeSlideImageTilt(undefined);
  }, [slideData]);

  const { state } = useScreenTheme();
  const [createReaction] = useCreateReactionMutation();
  const { variables: deviceSignals } = useDeviceSignalVariables();

  const handleReaction = (type: string) => {
    if (!createReaction) {
      return;
    }

    createReaction({
      variables: { submissionId: currentSlideId, type, ...deviceSignals },
    });
  };

  const reactions = (
    <FanReactionButtons reactionTypes={REACTIONS} onReaction={handleReaction} />
  );

  if (isFreeformLayout(slideData?.layout)) {
    return (
      <div
        data-name="FAN-SLIDES-ENGAGEMENT"
        className="flex flex-col items-center justify-center pt-4"
      >
        <FanFreeformSlideCard
          slideId={currentSlideId}
          slideData={slideData}
          entranceClassName={styles.enterFlat}
          themeFonts={{
            headingFontFamily: state.savedTheme.headingFontFamily,
            bodyFontFamily: state.savedTheme.bodyFontFamily,
          }}
          themeBackdrop={{
            backgroundImage: state.savedTheme.stageBackgroundImage
              ? `url(${state.savedTheme.stageBackgroundImage})`
              : undefined,
            backgroundColor: state.savedTheme.backgroundColor || '#111',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        {reactions}
      </div>
    );
  }

  return (
    <div
      data-name="FAN-SLIDES-ENGAGEMENT"
      className="flex flex-col items-center justify-center pt-4"
    >
      {imageUrl ? (
        <img
          className={cn(
            'relative z-10 -mb-36 h-[25vh] rounded bg-white p-2 shadow-sm transition-opacity duration-500',
            imageLoaded ? 'opacity-100' : 'opacity-0',
            imageLoaded && imageTilt === 'LEFT' && styles.tiltImageLeft,
            imageLoaded && imageTilt === 'RIGHT' && styles.tiltImageRight,
          )}
          src={imageUrl}
          alt="Slide Image"
        />
      ) : null}
      <Markdown className={styles.contentCard}>{content}</Markdown>
      {reactions}
    </div>
  );
};
